import DOMPurify from 'dompurify';

/**
 * Security utility functions for sanitizing user-generated content
 */

/**
 * Sanitizes HTML content to prevent XSS attacks
 * @param {string} dirtyHTML - The HTML content to sanitize
 * @param {Object} options - DOMPurify configuration options
 * @returns {string} - Sanitized HTML content
 */
export function sanitizeHTML(dirtyHTML, options = {}) {
  if (!dirtyHTML || typeof dirtyHTML !== 'string') {
    return '';
  }

  // Default configuration for TinyMCE content
  const defaultConfig = {
    // Allow common HTML elements that TinyMCE generates
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'span', 'div',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'a', 'img',
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'blockquote', 'pre', 'code',
      'hr', 'sub', 'sup', 'mark'
    ],
    
    // Allow common attributes
    ALLOWED_ATTR: [
      'href', 'title', 'alt', 'src', 'width', 'height',
      'style', 'class', 'id',
      'target', 'rel',
      'colspan', 'rowspan',
      'align', 'valign',
      'bgcolor', 'color', 'background-color', 'font-weight', 'text-align',
      'font-size', 'font-family', 'text-decoration'
    ],
    
    // Allow target="_blank" for links
    ALLOW_DATA_ATTR: false,
    
    // Keep comments for debugging in development
    KEEP_CONTENT: true,
    
    // Add security hooks
    FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input', 'button'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
    
    // Transform URLs to be safe
    SANITIZE_DOM: true,
    
    // Additional security measures
    ADD_TAGS: [],
    ADD_ATTR: [],
    
    // Ensure links are safe
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp|data):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i
  };

  // Merge default config with custom options
  const config = { ...defaultConfig, ...options };

  try {
    return DOMPurify.sanitize(dirtyHTML, config);
  } catch (error) {
    console.error('Error sanitizing HTML:', error);
    // Return empty string on error to prevent potential XSS
    return '';
  }
}

/**
 * Sanitizes HTML specifically for TinyMCE content
 * @param {string} content - TinyMCE generated content
 * @returns {string} - Sanitized content safe for v-html
 */
export function sanitizeTinyMCEContent(content) {
  return sanitizeHTML(content, {
    // Allow TinyMCE specific styling and table attributes
    ALLOWED_ATTR: [
      'href', 'title', 'alt', 'src', 'width', 'height',
      'style', 'class', 'id',
      'target', 'rel',
      'colspan', 'rowspan',
      'align', 'valign',
      'border', 'cellpadding', 'cellspacing',
      'bgcolor', 'color', 'background-color', 'font-weight', 'text-align',
      'border-color', 'border-width', 'border-style',
      'font-size', 'font-family', 'text-decoration',
      'data-mce-href', 'data-mce-style' // TinyMCE specific attributes
    ]
  });
}

/**
 * Sanitizes text content by stripping all HTML
 * @param {string} content - Content to strip HTML from
 * @returns {string} - Plain text content
 */
export function stripHTML(content) {
  if (!content || typeof content !== 'string') {
    return '';
  }

  try {
    return DOMPurify.sanitize(content, { ALLOWED_TAGS: [] });
  } catch (error) {
    console.error('Error stripping HTML:', error);
    return '';
  }
}

/**
 * Validates and sanitizes URLs
 * @param {string} url - URL to validate
 * @returns {string|null} - Sanitized URL or null if invalid
 */
export function sanitizeURL(url) {
  if (!url || typeof url !== 'string') {
    return null;
  }

  try {
    // Use DOMPurify to sanitize the URL
    const sanitized = DOMPurify.sanitize(url);
    
    // Additional URL validation
    const urlPattern = /^https?:\/\/([\w-]+\.)+[\w-]+(\/.*)?$/i;
    
    if (urlPattern.test(sanitized)) {
      return sanitized;
    }
    
    return null;
  } catch (error) {
    console.error('Error sanitizing URL:', error);
    return null;
  }
}

/**
 * Creates a safe configuration for DOMPurify based on content type
 * @param {string} contentType - Type of content ('notes', 'description', 'title', etc.)
 * @returns {Object} - DOMPurify configuration
 */
export function createSafeConfig(contentType = 'default') {
  const configs = {
    // For rich text notes from TinyMCE
    notes: {
      ALLOWED_TAGS: [
        'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'span', 'div',
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'ul', 'ol', 'li',
        'a', 'img',
        'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'colgroup', 'col',
        'blockquote', 'pre', 'code',
        'hr', 'sub', 'sup', 'mark'
      ],
      ALLOWED_ATTR: [
        'href', 'title', 'alt', 'src', 'style', 'class', 'target', 'rel',
        'border', 'cellpadding', 'cellspacing', 'width', 'height',
        'colspan', 'rowspan', 'align', 'valign',
        'bgcolor', 'color', 'background-color', 'font-weight', 'text-align',
        'border-color', 'border-width', 'border-style',
        'font-size', 'font-family', 'text-decoration'
      ]
    },
    
    // For simple descriptions
    description: {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'a'],
      ALLOWED_ATTR: ['href', 'title', 'target', 'rel']
    },
    
    // For titles (minimal formatting)
    title: {
      ALLOWED_TAGS: ['strong', 'em'],
      ALLOWED_ATTR: []
    },
    
    // Default safe configuration
    default: {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em'],
      ALLOWED_ATTR: []
    }
  };

  return configs[contentType] || configs.default;
}